import { useState, type FC } from 'react';
import type { StudentScientificProject } from '../types';
import {
  BookIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  FileTextIcon,
  ProjectIcon,
  StarIcon,
} from './CourseStudentIcons';

interface StudentScientificProjectsOverlayProps {
  projects: StudentScientificProject[];
  onBackToCabinet: () => void;
  onAddToPortfolio: (title: string, workType: string) => void;
}

type ProjectTab =
  | 'overview'
  | 'theme'
  | 'plan'
  | 'tasks'
  | 'literature'
  | 'documents'
  | 'meetings'
  | 'results';

export const StudentScientificProjectsOverlay: FC<StudentScientificProjectsOverlayProps> = ({
  projects,
  onBackToCabinet,
  onAddToPortfolio,
}) => {
  const [activeTab, setActiveTab] = useState<ProjectTab>('overview');

  const activeProject = projects[0] || {
    id: 'prj-1',
    project_code: 'PRJ-SCI-01',
    title: 'Student Corpus Lab',
    stage_title: 'Этап 2 · Сбор материала',
    direction: 'Исследовательский проект по сбору, разметке и анализу корпуса современной публичной речи.',
    progress_percent: 36,
    next_deadline: '2026-09-30',
    stage_timeline: 'сентябрь–декабрь 2026',
    my_role: 'Младший исследователь',
    actions: [
      { title: 'Собрать и описать 30 единиц материала', deadline: '2026-09-18', status: 'В работе' },
      { title: 'Проверить разметку и метаданные', deadline: '2026-09-30', status: 'Запланировано' },
      { title: 'Подготовить промежуточную презентацию', deadline: '2026-10-12', status: 'Запланировано' },
    ],
    is_active: true,
  };

  const navItems = [
    { id: 'overview' as const, label: 'Обзор проекта', icon: ProjectIcon },
    { id: 'theme' as const, label: 'Тема и роль', icon: StarIcon },
    { id: 'plan' as const, label: 'План и этапы', icon: CheckCircleIcon },
    { id: 'tasks' as const, label: 'Задачи и материалы', icon: FileTextIcon },
    { id: 'literature' as const, label: 'Литература проекта', icon: BookIcon },
    { id: 'documents' as const, label: 'Образцы и документы', icon: FileTextIcon },
    { id: 'meetings' as const, label: 'Встречи и консультации', icon: CalendarIcon },
    { id: 'results' as const, label: 'Презентации и результаты', icon: StarIcon },
  ];

  return (
    <div className="space-y-6 pb-12">
      <div className="border border-slate-800 bg-[#081321] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBackToCabinet}
            className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700/80 hover:border-cyan-400 text-slate-300 hover:text-cyan-400 flex items-center justify-center transition-all"
            aria-label="Вернуться в кабинет"
          >
            &larr;
          </button>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">
              Кабинет обучающегося
            </p>
            <h2 className="text-xl font-bold text-white tracking-wide">Научные проекты</h2>
            <p className="text-xs text-slate-400">
              Проекты, в которые вас добавил преподаватель: план · этапы · задачи · материалы · консультации · результаты.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onBackToCabinet}
          className="px-5 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md self-end sm:self-auto"
        >
          Сохранить
        </button>
      </div>

      <div className="p-4 rounded-xl bg-amber-950/40 border border-amber-500/40 text-amber-200 text-xs leading-relaxed">
        Здесь отображаются только научные проекты, в которые вас добавил преподаватель. Данные проекта общие для обоих кабинетов.
      </div>

      <div className="p-4 rounded-2xl bg-[#081524] border border-cyan-500/40 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
              {activeProject.stage_title}
            </span>
            <h3 className="text-lg font-bold text-white mt-0.5">{activeProject.title}</h3>
            <p className="text-xs text-slate-400">
              {activeProject.my_role} · {activeProject.progress_percent}%
            </p>
          </div>
          <span className="px-3 py-1 rounded-xl text-xs font-bold bg-cyan-950 text-cyan-300 border border-cyan-700/60 self-start sm:self-auto">
            {activeProject.project_code}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="border border-slate-800 bg-[#08121E] rounded-2xl p-2 space-y-1 shadow-lg">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-cyan-950/80 border border-cyan-500/60 text-cyan-300 shadow-md'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/60'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                    <div className="text-left">
                      <p>{item.label}</p>
                      <p className="text-[10px] font-normal text-slate-400">Научный проект</p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`w-3.5 h-3.5 ${isActive ? 'text-cyan-400' : 'text-slate-600'}`} />
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="lg:col-span-9 space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 p-5 rounded-2xl bg-[#081524] border border-cyan-500/30 space-y-5 shadow-xl">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                    АКТИВНЫЙ НАУЧНЫЙ ПРОЕКТ
                  </p>
                  <h3 className="text-2xl font-bold text-white mt-1">{activeProject.title}</h3>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    {activeProject.direction}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800">
                    <p className="text-[10px] text-slate-400">Прогресс</p>
                    <p className="text-2xl font-bold text-cyan-400 mt-1">
                      {activeProject.progress_percent}%
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">по проектному плану</p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800">
                    <p className="text-[10px] text-slate-400">Ближайший срок</p>
                    <p className="text-xl font-bold text-white mt-1">
                      {activeProject.next_deadline}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">следующая контрольная точка</p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800">
                    <p className="text-[10px] text-slate-400">Моя роль</p>
                    <p className="text-sm font-bold text-amber-400 mt-1">
                      {activeProject.my_role}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">в команде</p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800">
                    <p className="text-[10px] text-slate-400">Этап</p>
                    <p className="text-sm font-bold text-white mt-1">
                      {activeProject.stage_title}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {activeProject.stage_timeline}
                    </p>
                  </div>
                </div>

                <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-cyan-400 rounded-full"
                    style={{ width: `${activeProject.progress_percent}%` }}
                  ></div>
                </div>
              </div>

              <div className="lg:col-span-4 p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4 shadow-xl">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                    СЛЕДУЮЩИЕ ДЕЙСТВИЯ
                  </p>
                  <h4 className="text-base font-bold text-white mt-0.5">Что делать сейчас</h4>
                </div>

                <div className="space-y-3 pt-2">
                  {activeProject.actions.map((act) => (
                    <div
                      key={act.title}
                      className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800/80 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h5 className="text-xs font-bold text-white leading-snug">{act.title}</h5>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${
                            act.status === 'В работе'
                              ? 'bg-amber-950 text-amber-300 border-amber-700/60'
                              : 'bg-slate-800 text-slate-300 border-slate-700'
                          }`}
                        >
                          {act.status}
                        </span>
                      </div>
                      <p className="text-[11px] font-mono text-cyan-400">{act.deadline}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">Тема и исследовательская роль</h3>
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2">
                <span className="text-[10px] font-bold text-cyan-400 uppercase">РОЛЬ В ПРОЕКТЕ</span>
                <h4 className="text-base font-bold text-white">Младший исследователь: Сбор и разметка аудиоматериалов</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Обучающийся отвечает за фиксацию текстов публичных выступлений, фонологическую транскрипцию и сопоставление синтаксических конструкций с корпусом.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'plan' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">План и календарные этапы</h3>
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-emerald-400 uppercase">ЗАВЕРШЕНО</span>
                    <h4 className="text-sm font-bold text-white">Этап 1: Формирование методики и глоссария</h4>
                    <p className="text-xs text-slate-400">июнь–август 2026</p>
                  </div>
                  <span className="px-2 py-1 rounded bg-emerald-950 text-emerald-300 text-xs font-semibold">100%</span>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/60 border border-cyan-500/50 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-amber-400 uppercase">ТЕКУЩИЙ ЭТАП</span>
                    <h4 className="text-sm font-bold text-white">Этап 2: Сбор материала и экспертная разметка</h4>
                    <p className="text-xs text-slate-400">сентябрь–декабрь 2026</p>
                  </div>
                  <span className="px-2 py-1 rounded bg-amber-950 text-amber-300 text-xs font-semibold">36%</span>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between opacity-70">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">ЗАПЛАНИРОВАНО</span>
                    <h4 className="text-sm font-bold text-white">Этап 3: Подготовка научных публикаций и итогового отчёта</h4>
                    <p className="text-xs text-slate-400">январь–май 2027</p>
                  </div>
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-400 text-xs font-semibold">0%</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">Задачи и рабочие материалы</h3>
              <div className="space-y-3">
                {activeProject.actions.map((act) => (
                  <div key={act.title} className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-white">{act.title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">Срок: {act.deadline}</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                      {act.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'literature' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">Литература научного проекта</h3>
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <h4 className="text-sm font-bold text-white">Корпусные методы исследования функциональной грамматики</h4>
                <p className="text-xs text-slate-400">Байтұрсынов А. З. · Academia Press (2024)</p>
                <p className="text-[11px] font-mono text-cyan-400">DOI: 10.1016/j.lingua.2024.103</p>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">Образцы и документы проекта</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                  <h4 className="text-sm font-bold text-white">Паспорт научного проекта</h4>
                  <p className="text-xs text-slate-400">Техническое задание, цели и график этапов.</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                  <h4 className="text-sm font-bold text-white">Дневник исследовательской работы</h4>
                  <p className="text-xs text-slate-400">Фиксация еженедельных записей и результатов разметки.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'meetings' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <h3 className="text-lg font-bold text-white">Проектные встречи</h3>
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
                <span className="text-xs font-mono text-cyan-400 font-bold">2026-09-18 17:00</span>
                <h4 className="text-sm font-bold text-white">Рабочее совещание исследовательской группы</h4>
                <p className="text-xs text-slate-400">Обсуждение первых 30 размеченных фрагментов корпуса.</p>
              </div>
            </div>
          )}

          {activeTab === 'results' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Презентации и результаты</h3>
                  <p className="text-xs text-slate-400">
                    Утверждённые научные результаты можно добавить в академическое портфолио.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-900/80 border border-cyan-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <span className="text-[10px] font-bold text-cyan-400 uppercase">РЕЗУЛЬТАТ ЭТАПА 1</span>
                  <h4 className="text-sm font-bold text-white mt-0.5">
                    Реестр семантических категорий публичного дискурса
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Отчёт и аналитическая матрица разметки (35 стр., PDF)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onAddToPortfolio('Реестр семантических категорий публичного дискурса', 'Научный отчёт')
                  }
                  className="px-4 py-2 rounded-xl bg-amber-950 hover:bg-amber-900 text-amber-300 border border-amber-700/60 text-xs font-bold transition-all shrink-0"
                >
                  ★ В портфолио
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
